/*
 *   File : custom-tooltip.js
 *   Author : https://evoqins.com
 *   Description : tooltip component
 *   Integrations : NA
 *   Version : 1.0.0
*/

// packages
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';

// component
import Icon from '../Icon/icon';

// helper
import { getAppliedZoom } from '../../Helper/display-scale';

// style
import style from '../../Style/Component/tooltip.module.scss';

const GAP = 8;

const PinnedTooltip = (props) => {
    const anchorRef = useRef(null);
    const tipRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

    const place = useCallback(() => {
        const anchor = anchorRef.current;
        const tip = tipRef.current;
        if (!anchor || !tip) {
            return;
        }

        const rect = anchor.getBoundingClientRect();
        const tipRect = tip.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const top = (spaceBelow >= tipRect.height + GAP || spaceBelow >= spaceAbove)
            ? rect.bottom + GAP
            : rect.top - GAP - tipRect.height;
        const left = rect.left + (rect.width / 2) - (tipRect.width / 2);
        // html { zoom } scales fixed coordinates again. getBoundingClientRect is already visual px.
        const zoom = getAppliedZoom() || 1;

        setPos({ top: top / zoom, left: left / zoom, ready: true });
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            return undefined;
        }

        place();
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);

        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [open, props.content, place]);

    return (
        <>
            <span ref={anchorRef}
                className={style.e_tooltip}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}>
                <Icon icon='info'
                    size={props.size ? props.size : '0.75rem'}
                    color={props.color}
                    className='cursor-pointer d-flex e-outline-none' />
            </span>

            {open && props.content && typeof document !== 'undefined' &&
                createPortal(
                    <div ref={tipRef}
                        className={style.e_custom_tooltip}
                        style={{
                            position: 'fixed',
                            top: pos.top,
                            left: pos.left,
                            width: props.width || 'max-content',
                            maxWidth: props.maxWidth || '320px',
                            zIndex: 9999,
                            visibility: pos.ready ? 'visible' : 'hidden',
                            pointerEvents: 'none',
                            margin: 0,
                        }}>
                        {props.content}
                    </div>,
                    document.body
                )}
        </>
    );
};

const CustomTooltip = (props) => {
    if (props.pinned) {
        return <PinnedTooltip {...props} />;
    }

    const showIcon = props.icon !== false;
    const tooltip = (
        <Tooltip id={props.id}
            place={props.place || 'bottom-start'}
            offset={props.offset}
            positionStrategy={props.positionStrategy}
            middlewares={props.middlewares}
            className={style.e_custom_tooltip}
            style={{
                width: props.width || 'auto',
                maxWidth: props.maxWidth,
                padding: props.padding,
                zIndex: 9999,
            }}
        />
    );

    return (
        <div className={style.e_tooltip}
            data-tooltip-id={showIcon ? props.id : undefined}
            data-tooltip-content={showIcon ? props.content : undefined}>
            {showIcon &&
                <Icon icon='info'
                    size={props.size ? props.size : '0.75rem'}
                    color={props.color}
                    className='cursor-pointer d-flex e-outline-none' />
            }

            {props.portal && typeof document !== 'undefined'
                ? createPortal(tooltip, document.body)
                : tooltip}
        </div>
    );
}

export default CustomTooltip;
